import { FontAwesomeIcon } from "@fortawesome/react-fontawesome";
import { faSpinner } from "@fortawesome/sharp-regular-svg-icons";
import { useMutation, useQueryClient } from "@tanstack/react-query";
import { useDocumentTitle } from "@uidotdev/usehooks";
import { useAtom } from "jotai";
import { useEffect, useMemo, useRef } from "react";
import { useNavigate, useSearchParams } from "react-router-dom";
import type {
    EmissionRecordsEmissionCountsListDataSourceEnum,
    EmissionRecordView,
} from "../../apiClient/generated";
import {
    useEmissionEventsApiClient,
    useEmissionRecordsApiClient,
} from "../../hooks";
import { ROUTES } from "../../routes";
import { DashboardCard } from "../../ui/Card";
import {
    EmissionApiDataTableV2,
    emissionTableFiltersAtom,
} from "../Emissions/EmissionsApiDataTableV2";
import { EmissionDashboardStatsPanel } from "./EmissionStats";

/** Hook for pointing to the main event.
 *
 * Fetches emission, check if it is the main one,
 * return main one if not.
 *
 * @param emissionRecord: EmissionRecordView
 * @param emissionRecordId: string (emission id)
 */
const useMainEmissionRecord = () => {
    const apiClient = useEmissionRecordsApiClient();
    const eventsApiClient = useEmissionEventsApiClient();
    const queryClient = useQueryClient();

    const resolveMainRecord = useMutation({
        mutationFn: async (
            input: string | EmissionRecordView,
        ): Promise<EmissionRecordView> => {
            const record =
                typeof input === "string"
                    ? await queryClient.fetchQuery({
                          queryKey: ["emissionRecord", input],
                          queryFn: () =>
                              apiClient.emissionRecordsRetrieve({ id: input }),
                      })
                    : input;

            if (!record.mainEvent?.mainEvent) {
                return record;
            }

            const mainEvent = await eventsApiClient.emissioneventsRetrieve({
                id: record.mainEvent.mainEvent,
            });
            const mainRecord = await queryClient.fetchQuery({
                queryKey: [
                    "emissionRecordForEvent",
                    record.mainEvent.mainEvent,
                ],
                queryFn: () =>
                    apiClient.emissionRecordsRetrieve({
                        id: mainEvent.mainEmissionRecord,
                    }),
            });
            return mainRecord;
        },
    });
    return { resolveMainRecord };
};

const buildSitePageUrl = (emissionRecord: EmissionRecordView) => {
    const siteId = emissionRecord.site
        ? emissionRecord.site.id
        : emissionRecord.siteMatches?.[0]?.id;
    return `${ROUTES.INFRASTRUCTURE_SITE}?site=${siteId}&selectedContext=${emissionRecord.id}&eventForm=${emissionRecord.id}`;
};

interface EmissionsDashboardProps {
    title: string;
    className: string;
    emissionType?: EmissionRecordsEmissionCountsListDataSourceEnum;
}

export const EmissionsDashboardV2 = (props: EmissionsDashboardProps) => {
    useDocumentTitle(`${props.title} - AERSHED`);
    const [searchParams] = useSearchParams();
    const [filters] = useAtom(emissionTableFiltersAtom);
    const navigate = useNavigate();
    const { resolveMainRecord } = useMainEmissionRecord();

    const selectedEvent = useMemo(() => {
        return searchParams.get("selectedEvent");
    }, [searchParams]);

    const redirectedEventRef = useRef<string | null>(null);
    useEffect(() => {
        const redirect = async () => {
            redirectedEventRef.current = selectedEvent;
            try {
                const mainRecord =
                    await resolveMainRecord.mutateAsync(selectedEvent);
                const url = buildSitePageUrl(mainRecord);
                navigate(url, { replace: true });
            } catch {
                redirectedEventRef.current = null;
                resolveMainRecord.reset();
            }
        };
        if (selectedEvent && redirectedEventRef.current !== selectedEvent) {
            redirectedEventRef.current = selectedEvent;
            redirect();
        }
    }, [selectedEvent, resolveMainRecord, navigate]);

    return (
        <div>
            <div className="mb-6" />
            <DashboardCard
                flair={{
                    label: props.title,
                    className: props.className,
                }}
            >
                <EmissionDashboardStatsPanel
                    title={props.title}
                    filters={{
                        ...filters,
                        dataSource: props.emissionType
                            ? [props.emissionType]
                            : undefined,
                    }}
                />
            </DashboardCard>
            <DashboardCard>
                <EmissionApiDataTableV2
                    dataSource={
                        props.emissionType ? [props.emissionType] : undefined
                    }
                    onClickRow={(_id, emissionRecord) => {
                        // Opens tab right away to avoid pop-up blocker!
                        const tab = window.open("about:blank");
                        // Then run mutation and update URL once requests are done.
                        resolveMainRecord.mutate(emissionRecord, {
                            onSuccess: (record) => {
                                const url = buildSitePageUrl(record);
                                if (tab) {
                                    tab.location.href = url;
                                } else {
                                    window.open(url);
                                }
                            },
                            onError: () => tab?.close(),
                        });
                    }}
                />
            </DashboardCard>

            {/* Modal window */}
            {(selectedEvent || resolveMainRecord.isPending) && (
                <div className="absolute top-0 left-0 w-full h-full bg-neutral-400 bg-opacity-60 flex items-center justify-center gap-4">
                    <FontAwesomeIcon
                        icon={faSpinner}
                        className="h-6 animate-spin"
                    />
                    Loading emission...
                </div>
            )}
        </div>
    );
};
